import { Component, OnDestroy, OnInit } from '@angular/core';
import { AuthService } from 'src/app/core/services/auth.service';
import { Router } from '@angular/router';
import { AppToastService } from 'src/app/shared/services/app-toast.service';
import { UntypedFormBuilder, Validators, UntypedFormGroup, ValidatorFn, AbstractControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { User } from 'src/app/core/models/user';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { AuthLayoutComponent } from '../auth-layout/auth-layout.component';
import { SocialAuthService, SocialUser, GoogleSigninButtonModule } from '@abacritt/angularx-social-login';
import { Subscription } from 'rxjs';
import { MsalService } from '@azure/msal-angular';

@Component({
  selector: 'app-register-page',
  templateUrl: './register-page.component.html',
  styleUrls: ['./register-page.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, ButtonModule, InputTextModule, AuthLayoutComponent, GoogleSigninButtonModule],
})
export class RegisterPageComponent implements OnInit, OnDestroy {
  registerForm: UntypedFormGroup;
  showPassword = false;
  showConfirmPassword = false;
  private authSubscription: Subscription;

  constructor(
    private authService: AuthService,
    private router: Router,
    private socialAuthService: SocialAuthService,
    private readonly toastService: AppToastService,
    private msalService: MsalService,
    private fb: UntypedFormBuilder
  ) { }

  async ngOnInit() {
    this.createRegisterform();
    await this.msalService.initialize();

    this.authSubscription = this.socialAuthService.authState.subscribe({
      next: (user: SocialUser) => {
        if (user) this.handleGoogleRegister(user);
      },
      error: (err: any) => console.error('Google Register Error:', err)
    });
  }

  ngOnDestroy() {
    if (this.authSubscription) this.authSubscription.unsubscribe();
  }

  createRegisterform() {
    this.registerForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required, Validators.minLength(4), Validators.maxLength(16)]],
      confirmPassword: ['', [Validators.required, this.matchValues('password')]]
    });
  }

  matchValues(matchTo: string): ValidatorFn {
    return (control: AbstractControl) => {
      const parent = control.parent;
      return parent && control.value === parent.get(matchTo)?.value ? null : { isMatching: true };
    };
  }

  register() {
    if (this.registerForm.valid) {
      const user: User = Object.assign({}, this.registerForm.value);
      user.userName = user.email;

      this.authService.register(user).subscribe({
        next: () => {
          this.toastService.success('Success', 'Confirmation link sent to your email.');
          this.router.navigate(['login']);
        },
        error: () => { },
      });
    }
  }

  handleGoogleRegister(user: SocialUser) {
    this.authService.googleLogin(user.idToken).subscribe({
      next: () => {
        this.toastService.success('Success', 'Registered with Google!');
        this.router.navigate(['/home']);
      },
      error: () => { },
    });
  }

  microsoftLogin() {
    this.msalService.instance.loginPopup({ scopes: ['User.Read'] })
      .then((result) => {
        if (!result?.accessToken) {
          this.toastService.error('Microsoft token was not returned.', 'Error');
          return;
        }

        this.authService.microsoftLogin(result.accessToken).subscribe({
          next: () => {
            this.toastService.success('Registered with Microsoft!', 'Success');
            this.router.navigate(['/home']);
          },
          error: (err: any) => {
            this.toastService.error(err?.error || err || 'Microsoft login failed.', 'Error');
          }
        });
      })
      .catch((err: any) => {
        this.toastService.error(err?.message || 'Microsoft login failed.', 'Error');
      });
  }
}
